<template>
  <div class="cx-date-time-picker">
    <VueDatePicker
      id="date-time-picker"
      ref="datepicker"
      :dark="true"
      :preventMinMaxNavigation="true"
      :modelValue="modelValue"
      :minDate="minDate"
      :maxDate="innerMaxDate"
      :range="false"
      :markers="activeDays"
      :autoApply="autoApply"
      :keepActionRow="keepActionRow"
      :closeOnAutoApply="closeOnAutoApply"
      :format="dateFormatter"
      :clearable="false"
      :disabled="disabled || loading"
      :enableTimePicker="enableTimePicker"
      :timePickerInline="true"
      :onClickOutside="handleClickOutside"
      @update-month-year="handleChangeMonthYear"
      @update:model-value="updateModelValue"
      @open="resetSelectedMonthYear"
      @closed="onCloseDatePicker">
      <template #dp-input="{ value }">
        <slot name="activator" :value="value">
          <cx-date-picker-activator :value="value" :disabled="disabled" :loading="loading" :placeholder="placeholder" :label="label"/>
        </slot>
      </template>
      <template #month-overlay-value="{ value, text }">
        <cx-month-cell :monthText="text" :monthValue="value" :isActiveMonth="isActiveMonth(value)" :selectedYear="selectedYear"/>
      </template>
      <template #year-overlay-value="{ value }">
        <cx-year-cell :yearValue="value" :isActiveYear="isActiveYear(value)"/>
      </template>
    </VueDatePicker>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, ComputedRef, } from 'vue';
import VueDatePicker, { DatePickerMarker, DatePickerInstance } from '@vuepic/vue-datepicker';
import CxDatePickerActivator from './CxDatePickerActivator.vue';
import CxMonthCell from './CxMonthCell.vue';
import CxYearCell from './CxYearCell.vue';
import '@vuepic/vue-datepicker/dist/main.css';
import datetimehelper from '@/utils/datetimehelper';
import { endOfDay } from 'date-fns';

interface Props {
  modelValue?: Date | null,
  minDate?: Date,
  maxDate?: Date
  events?: Date[],
  autoApply?: boolean,
  keepActionRow?: boolean,
  closeOnAutoApply?: boolean,
  dateFormat?: string,
  disabled?: boolean,
  enableTimePicker?: boolean,
  hideOnClickOutside?: boolean,
  loading?: boolean,
  placeholder?: string,
  label?: string,
  allowFutureDates?: boolean,
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: () => new Date(),
  events: () => [],
  autoApply: false,
  keepActionRow: false,
  closeOnAutoApply: true,
  disabled: false,
  enableTimePicker: true,
  hideOnClickOutside: true,
  loading: false,
  allowFutureDates: false,
});

const emit = defineEmits<{
  (e: 'update:modelValue', value: Date),
  (e: 'toggleDatePickerOpen', value: boolean),
  (e: 'onApply', value: Date),
 }>();

const datepicker = ref<DatePickerInstance>(null);

const updateModelValue = (newValue: Date) => {
  emit('onApply', newValue);
  emit('update:modelValue', newValue);
};

const innerMaxDate = computed(() => {
  if (props.allowFutureDates) {
    return props.maxDate ? endOfDay(props.maxDate) : undefined;
  }
  return new Date();
});

const innerDateFormat = computed(() => {
  if (props.dateFormat) {
    return props.dateFormat;
  }
  if (props.enableTimePicker) {
    return datetimehelper.LOCALE_DATE_TIME_FORMAT;
  }
  return datetimehelper.LOCALE_DATE_FORMAT;
});

const dateFormatter = (dateToFormat: Date) => {
  return datetimehelper.format(dateToFormat, innerDateFormat.value);
};

const selectedMonth = ref<number>(props.modelValue === null ? new Date().getMonth() : props.modelValue.getMonth());
const selectedYear = ref<number>(props.modelValue === null ? new Date().getFullYear() : props.modelValue.getFullYear());

const activeDays: ComputedRef<DatePickerMarker[]> = computed(() => props
  .events.map(event => ({ date: event, type: 'dot' })));

const isActiveMonth = (month: number) => {
  const activeMonths = activeDays.value
    .filter(a => new Date(a.date).getFullYear() === selectedYear.value)
    .map(a => new Date(a.date).getMonth());
  return activeMonths.includes(month);
};

const isActiveYear = (year: number) => {
  const activeYears = activeDays.value
    .map(a => new Date(a.date).getFullYear());
  return activeYears.includes(year);
};

const handleChangeMonthYear = ({ month, year }: { month: number, year: number }) => {
  selectedMonth.value = month;
  selectedYear.value = year;
};

const resetSelectedMonthYear = () => {
  selectedMonth.value = props.modelValue === null ? new Date().getMonth() : props.modelValue.getMonth();
  selectedYear.value = props.modelValue === null ? new Date().getFullYear() : props.modelValue.getFullYear();
  emit('toggleDatePickerOpen', true);
};

const onCloseDatePicker = () => {
  emit('toggleDatePickerOpen', false);
};

const handleClickOutside = () => {
  if (props.hideOnClickOutside && datepicker.value) {
    datepicker.value.closeMenu();
  }
};

</script>

<style lang="less">
@import './vue-datepicker.less';

.cx-date-time-picker {
  width: 100%;
}

</style>